import { useMemo, useState, useCallback, useEffect } from 'react';
import { makeStyles, Typography, IconButton, TextField, Tooltip } from '@material-ui/core';
import { Add as AddIcon, Clear as ClearIcon, Delete as DeleteIcon } from '@material-ui/icons';
import cx from 'classnames';
import { alpha } from '@material-ui/core/styles';
import { Field } from 'formik';
import CurrencyFormat from 'react-currency-format';
import { useDispatch, useSelector } from 'react-redux';
import { setAccountBackendErrors, setBackendErrors } from '../../../../../../../../../redux_store';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
  },
  fieldsRow: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  textField: {
    maxWidth: 600,
    height: 44,
    minHeight: 44,
    zIndex: 0,
    padding: '18.5px 14px',
    border: `1px solid ${alpha('#818FA0', 0.25)}`,
    borderRadius: 10,
    fontSize: theme.spacing(2),
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    width: '100%',
  },
  addButton: {
    padding: 4,
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
    transition: 'all 0.1s ease-in-out',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
      transform: 'scale(1.1)',
    },
  },
  addButtonContainer: {
    transition: 'all 0.1s ease-in',
    opacity: 1,
    width: 32,
    marginRight: theme.spacing(0.5),
  },
  addButtonContainerNoTransition: {
    opacity: 1,
    width: 32,
    marginRight: theme.spacing(0.5),
  },
  addButtonHidden: {
    opacity: 0,
    width: 0,
    marginRight: 0,
    overflow: 'hidden',
  },
  addButtonHiddenNoTransition: {
    opacity: 0,
    width: 0,
    marginRight: 0,
    overflow: 'hidden',
  },
  initialPriceContainer: {
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s ease-in',
    maxWidth: 0,
    opacity: 0,
  },
  initialPriceContainerNoTransition: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 0,
    opacity: 0,
  },
  initialPriceContainerVisible: {
    maxWidth: 300,
    opacity: 1,
    marginRight: 12,
  },
  initialPriceContainerVisibleNoTransition: {
    maxWidth: 300,
    opacity: 1,
    marginRight: 12,
  },
  error: {
    border: `1px solid ${theme.palette.error.main}`,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.75rem',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 400,
    lineHeight: 1.66,
    letterSpacing: '0.03333em',
    position: 'absolute',
    bottom: -2,
  },
  initialPriceWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: 140,
  },
  initialPriceField: {
    height: 44,
    maxHeight: 44,
    minHeight: 44,
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: 44,
      maxHeight: 44,
      minHeight: 44,
      fontSize: '0.875rem',
      paddingRight: 36, // Space for delete button
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '0.875rem',
      transform: 'translate(14px, 14px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(129, 143, 160, 0.25)',
    },
  },
  deleteButtonInside: {
    position: 'absolute',
    right: 2,
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 6,
    color: theme.palette.text.secondary,
    transition: 'all 0.2s ease-in-out',
    zIndex: 1,
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: 'transparent',
    },
  },
  clearButtonInside: {
    position: 'absolute',
    right: 22,
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 6,
    color: theme.palette.text.secondary,
    transition: 'all 0.2s ease-in-out',
    zIndex: 1,
    '&:hover': {
      color: theme.palette.error.main,
      backgroundColor: 'transparent',
    },
  },
  deleteButton: {
    padding: 4,
    color: theme.palette.error.main,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.04),
      transform: 'scale(1.1)',
    },
  },
}));

const PriceComponent = (props) => {
  const classes = useStyles();
  const {
    row,
    row: { index: i, original },
    updateFieldValue,
  } = props;
  const index = parseInt(i);
  const name = `ispConfiguration.services[${index}].price`;
  const initialPriceName = `ispConfiguration.services[${index}].initialPrice`;
  // const daysToUseSecondaryPriceName = `ispConfiguration.services[${index}].daysToUseSecondaryPrice`;
  const [showExtraRow, setShowExtraRow] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const dispatch = useDispatch();
  const { backendErrors } = useSelector((state) => state.accountEditing);
  const setBackendErrors = (newErrors) => {
    dispatch(setAccountBackendErrors(newErrors));
  };
  const onUpdateFieldValue = async (key, value) => {
    updateFieldValue(index, 'price', value.replaceAll(',', ''));
    if (backendErrors) {
      if (backendErrors[`ispConfiguration.services[${index}]`]) {
        const newBackendErrors = { ...backendErrors };
        delete newBackendErrors[`ispConfiguration.services[${index}]`];
        setBackendErrors(newBackendErrors);
      } else if (backendErrors?.ispConfiguration?.services && backendErrors?.ispConfiguration?.services[index].price) {
        const newErrors = {
          ...backendErrors,
          ispConfiguration: {
            ...backendErrors.ispConfiguration,
            services: backendErrors.ispConfiguration.services.map((service, i) => {
              if (i === index) {
                const newError = { ...service };
                delete newError?.price;
                return newError;
              } else {
                return service;
              }
            }),
          },
        };
        if (!newErrors.ispConfiguration.services.length) {
          delete newErrors.ispConfiguration.services;
        }
        setBackendErrors(newErrors);
      }
    }
  };

  const errorsText = useMemo(
    () => (backendErrors?.ispConfiguration?.services &&
      (backendErrors.ispConfiguration.services[index] &&
        backendErrors.ispConfiguration.services[index].price)
    ),
    [backendErrors],
  );

  const errors = useMemo(
    () => (backendErrors
        ? (errorsText || backendErrors[`ispConfiguration.services[${index}]`])
        : null
    ),
    [backendErrors, errorsText],
  );

  const handleAddClick = () => {
    setIsInitialRender(false);
    setShowExtraRow(true);
  };

  const handleDeleteExtra = async (form) => {
    setIsInitialRender(false);
    setTimeout(() => {
      updateFieldValue(index, 'initialPrice', null);
      form.setFieldValue(initialPriceName, '');
      setTimeout(() => {
        setShowExtraRow(false);
      }, 100);
    }, 100);
  };

  const handleInitialPriceChange = (value) => {
    const cleanValue = value.replace(/[^\d.]/g, '');
    updateFieldValue(index, 'initialPrice', cleanValue);
  };

  // const handleDaysToUseSecondaryPriceChange = (value) => {
  //   const cleanValue = value.replace(/[^\d]/g, '');
  //   updateFieldValue(index, 'daysToUseSecondaryPrice', cleanValue);
  // };

  const PriceInput = useCallback((props) => (
    <TextField
      {...props}
      variant="outlined"
      size="small"
      label="Initial Price"
    />
  ), []);

  useEffect(() => {
    const hasExtraData = (original.initialPrice && original.initialPrice !== '');
    if (hasExtraData && !showExtraRow) {
      setShowExtraRow(true);
    }
  }, [original, showExtraRow]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: '100%', padding: '8px 0' }}>
      <Field type="number" name={name}>
        {({ field, form }) => (
          <Field name={initialPriceName}>
            {({ field: initialPriceField }) => (
              <div className={classes.container}>
                <div className={classes.fieldsRow}>
                  {/* Add button - shows when no Initial Price */}
                  <div className={cx(
                    isInitialRender ? classes.addButtonContainerNoTransition : classes.addButtonContainer,
                    {
                      [isInitialRender ? classes.addButtonHiddenNoTransition : classes.addButtonHidden]: showExtraRow
                    }
                  )}>
                    <Tooltip
                      title={<Typography style={{ fontSize: '.75rem' }}>Add Initial Price</Typography>}
                      placement="top"
                      arrow
                    >
                      <IconButton
                        className={classes.addButton}
                        onClick={handleAddClick}
                        size="small"
                        aria-label="Add initial price"
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>

                  {/* Initial Price field - shows when button clicked */}
                  <div className={cx(
                    isInitialRender ? classes.initialPriceContainerNoTransition : classes.initialPriceContainer,
                    {
                      [isInitialRender ? classes.initialPriceContainerVisibleNoTransition : classes.initialPriceContainerVisible]: showExtraRow
                    }
                  )}>
                    <div className={classes.initialPriceWrapper}>
                      <CurrencyFormat
                        className={classes.initialPriceField}
                        customInput={PriceInput}
                        prefix={'$ '}
                        value={initialPriceField.value || ''}
                        allowNegative={false}
                        fixedDecimalScale={true}
                        decimalScale={2}
                        onChange={(e) => {
                          const value = e.target.value.replace('$ ', '');
                          handleInitialPriceChange(value);
                          form.setFieldValue(initialPriceName, value);
                        }}
                        thousandSeparator={','}
                        placeholder="$ 0.00"
                      />
                        <IconButton
                          className={classes.clearButtonInside}
                          onClick={(e) => {
                            handleInitialPriceChange('');
                            form.setFieldValue(initialPriceName, '');
                          }}
                          size="small"
                          aria-label="Clear initial price"
                        >
                          <ClearIcon style={{ fontSize: 18 }} />
                        </IconButton>
                        <IconButton
                          className={classes.deleteButtonInside}
                          onClick={() => handleDeleteExtra(form)}
                          size="small"
                          aria-label="Remove initial price"
                        >
                          <DeleteIcon style={{ fontSize: 18 }} />
                        </IconButton>
                    </div>
                  </div>

                  {/* Main Price field */}
                  <div className={classes.inputWrapper}>
                    <CurrencyFormat
                      className={cx(classes.textField, {
                        [classes.error]: errors,
                      })}
                      prefix={'$ '}
                      value={field.value}
                      allowNegative={false}
                      fixedDecimalScale={true}
                      decimalScale={2}
                      onChange={async (e) => {
                        const value = e.target.value.replace('$ ', '');
                        await form.setFieldValue(name, value);
                        await onUpdateFieldValue(name, value);
                      }}
                      thousandSeparator={','}
                      error={errors}
                    />
                  </div>
                </div>
                {!!errorsText && (
                  <Typography className={cx(classes.errorText)}>
                    {errorsText}
                  </Typography>
                )}
              </div>
            )}
          </Field>
        )}
      </Field>
    </div>
  );
};

export default PriceComponent;
